import type { JSX } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import { Text } from '../Text';
import { Stack } from '../Stack';
import { Icon } from '../Icon';
import { RadioCardIndicator } from './RadioCardIndicator';

const StyledCardContent = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: var(--wui-space-02);
`;

const StyledCardIcon = styled.div`
  display: contents;
`;

const StyledGatedIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 1;
`;

const StyledIndicatorContainer = styled.div<{ $hasIcon: boolean }>`
  height: ${({ $hasIcon }) => ($hasIcon ? '24px' : '16px')};
  display: flex;
  align-items: center;
`;

export type RadioCardDefaultLayoutProps = {
  /**
   * The icon to display
   */
  icon?: JSX.Element | undefined;
  /**
   * The main label text for the card.
   *
   *  *Note: `label` only allows for a string as any interactive elements inside of a RadioCard would be an accessibility violation.*
   */
  label?: string | undefined;
  /**
   * The description text for the card
   *
   * *Note: `description` only allows for a string as any interactive elements inside of a RadioCard would be an accessibility violation.*
   */
  description?: string | undefined;
  /**
   * Whether to show the indicator
   */
  showIndicator?: boolean | undefined;
  /**
   * Whether to show the sparkle icon and gated styles
   */
  isGated?: boolean | undefined;
};

export const RadioCardDefaultLayout = ({
  icon,
  label,
  description,
  showIndicator = true,
  isGated = false,
}: RadioCardDefaultLayoutProps): JSX.Element => {
  return (
    <StyledCardContent>
      {showIndicator ? (
        <StyledIndicatorContainer $hasIcon={isNotNil(icon)}>
          <RadioCardIndicator data-testid="wui-radio-card-indicator" />
        </StyledIndicatorContainer>
      ) : null}
      {isGated ? (
        <StyledGatedIcon data-testid="wui-radio-gated-icon">
          <Icon
            colorScheme="purple"
            data-testid="wui-icon-sparkle"
            type="sparkle"
          />
        </StyledGatedIcon>
      ) : null}
      <Stack gap="space-02">
        {isNotNil(icon) && <StyledCardIcon data-wui-radio-card-icon={true}>{icon}</StyledCardIcon>}
        <Stack
          gap="space-01"
          style={isNotNil(icon) ? { paddingLeft: 2 } : undefined}
        >
          {isNotNil(label) && (
            <Text variant="label3">
              <strong>{label}</strong>
            </Text>
          )}
          {isNotNil(description) && (
            <Text
              prominence="secondary"
              variant="body3"
            >
              {description}
            </Text>
          )}
        </Stack>
      </Stack>
    </StyledCardContent>
  );
};

RadioCardDefaultLayout.displayName = 'RadioCardDefaultLayout';
